import { useParams, Navigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/outline";
import {
  createPreRegistration,
  getLandingPageBySlug,
} from "../../api/landingPageApi";
import Select from "react-select";
import { allCountries } from "country-telephone-data";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import {
  GoogleMap,
  Marker,
  LoadScript,
  useLoadScript,
} from "@react-google-maps/api";
import { serviceKeys } from "../../constants";
import map_pin from "../../assets/map_pin.png";
import NotFound from "../notFound/NotFoundPage";
import logo from "../../assets/logo.png";
import { useTranslation } from "react-i18next";

const getFlagEmoji = (countryCode) =>
  countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));

const countryOptions = allCountries.map((country) => ({
  label: `${getFlagEmoji(country.iso2)} ${country.name} (+${country.dialCode})`,
  value: `+${country.dialCode}`,
}));

// const countryOptions = allCountries.map((country) => ({
//   label: `${country.name} (+${country.dialCode})`,
//   value: `+${country.dialCode}`,
// }));

const RESERVED_ROUTES = [
  "login",
  "users",
  "artists",
  "landing-pages",
  "bookings",
  "clients",
];

const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "ro", label: "Română", flag: "🇷🇴" },
];

const PublicLandingPage = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();

  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const [successData, setSuccessData] = useState(null);
  const [countdown, setCountdown] = useState(3);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const currentLang =
    LANGUAGES.find((l) => i18n.language?.startsWith(l.code)) || LANGUAGES[0];

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: serviceKeys.API_KEY,
  });

  useEffect(() => {
    getLandingPageBySlug(slug)
      .then(setData)
      .catch(() => setNotFound(true));
  }, [slug]);

  // get translated info based on current language
  const getInfo = () => {
    const lang = i18n.language?.startsWith("es")
      ? "es"
      : i18n.language?.startsWith("ro")
        ? "ro"
        : "en";
    return data?.[`info_${lang}`] || data?.info_en || data?.info || "";
  };

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+34",
    acceptTerms: false,
    acceptPrivacy: false,
  });

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePhoneChange = (value) => {
    // Remove non-digits
    let cleaned = value.replace(/\D/g, "");

    handleChange("phone", cleaned);
  };

  const isFormValid =
    form.name &&
    form.email &&
    form.phone &&
    form.acceptTerms &&
    form.acceptPrivacy;

  const handleSubmit = async () => {
    if (!isFormValid) return;

    try {
      setSubmitting(true);

      const formattedPhone = form.phone.replace(/^0+/, "");

      const payload = {
        full_name: form.name,
        email: form.email,
        phone: `${form.countryCode}${formattedPhone}`,
        accepted_terms: true,
        accepted_privacy: true,
      };

      // await createPreRegistration(slug, payload);

      // setToast({
      //   type: "success",
      //   message: "Registration successful!",
      // });

      await createPreRegistration(slug, payload);

      setSuccessData({
        whatsapp_link: data.whatsapp_link,
      });

      setForm({
        name: "",
        email: "",
        phone: "",
        countryCode: "+34",
        acceptTerms: false,
        acceptPrivacy: false,
      });
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || t("landing.errorGeneric"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!successData) return;

    if (countdown <= 0) return;

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [successData, countdown]);

  useEffect(() => {
    if (!successData || countdown !== 0) return;

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile) {
      window.location.href = successData.whatsapp_link;
    } else {
      window.open(successData.whatsapp_link, "_blank");
    }
  }, [countdown, successData]);

  // 🔒 Prevent conflicts with system routes
  if (RESERVED_ROUTES.includes(slug)) {
    return <Navigate to="/" replace />;
  }

  if (notFound) {
    return <NotFound />;
  }

  if (!data) {
    return <div className="text-white p-10">{t("landing.loading")}</div>;
  }

  if (successData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl p-6 text-center shadow-xl">
          {/* close */}
          <button
            onClick={() => setSuccessData(null)}
            className="absolute top-3 right-3 text-slate-400 hover:text-white text-lg"
          >
            ✕
          </button>

          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-pink-600/20">
            <span className="text-2xl text-pink-500">✓</span>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-5">
            {t("landing.thankYou")}
          </h1>

          {/* Redirect text */}
          <p className="text-slate-300 font-bold text-2xl mb-2">
            {t("landing.redirectingWhatsapp")}
          </p>

          <p className="text-slate-400 text-sm mb-4">
            {t("landing.sendMessageWhatsapp")}
          </p>

          {/* Countdown */}
          <div className="text-3xl font-bold text-pink-500 mb-6">
            {countdown}
          </div>

          {/* Manual button */}
          {/* {countdown === 0 && (
            
              href={data.whatsapp_link}
              className="inline-block w-full py-3 rounded-lg bg-pink-600 hover:bg-pink-700 text-white font-medium transition"
            >
              Continue Now!
            </a>
          )} */}
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="bg-[#05261c] border border-green-900/30 rounded-xl h-[200px] flex items-center justify-center">
        <p className="text-green-400">{t("landing.loadingMap")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-6 md:px-10 md:py-10">
      {/* HEADER */}
      <div className="fixed top-0 left-0 w-full bg-slate-900/90 backdrop-blur-md border-b border-white/10 z-20 flex items-center justify-between px-6 py-3">
        {/* LEFT — LOGO or BRAND */}
        <img src={logo} alt="Afrodise" className="h-6 object-contain" />

        {/* RIGHT — LANGUAGE DROPDOWN */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen((v) => !v)}
            className="flex items-center gap-1.5 text-xs tracking-widest uppercase text-slate-400 hover:text-white transition"
          >
            <span>{currentLang.flag}</span>
            <span>{currentLang.code.toUpperCase()}</span>
            <ChevronDownIcon
              className={`w-3 h-3 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
            />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-8 bg-slate-900 border border-white/10 rounded-xl overflow-hidden shadow-xl z-50 min-w-[140px]">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    i18n.changeLanguage(lang.code);
                    setDropdownOpen(false);
                  }}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs tracking-widest uppercase transition
            ${
              i18n.language?.startsWith(lang.code)
                ? "text-pink-400 bg-pink-500/10"
                : "text-slate-400 hover:text-white hover:bg-white/5"
            }`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* OFFSET FOR FIXED HEADER */}
      <div className="pt-10" />

      {/* Flyer */}
      {data.flyer && (
        <img
          src={data.flyer}
          alt="flyer"
          className="w-full max-h-[250px] md:max-h-[400px] object-contain rounded-xl"
        />
      )}

      {/* Content */}
      <div className="max-w-2xl mx-auto px-2 md:px-6 py-6 md:py-10">
        <h1 className="text-3xl text-center font-bold">{data.title}</h1>
        <div
          className="info-content mt-4 text-slate-300"
          dangerouslySetInnerHTML={{
            __html: getInfo().replace(/&nbsp;/g, " "),
          }}
        />
      </div>

      {data.map_latitude && data.map_longitude && (
        <div className="max-w-3xl mx-auto mb-6 bg-slate-900 border border-white/10 rounded-xl p-4 md:p-5 text-left">
          {/* Address */}
          <p className="text-sm text-slate-400 mb-2">{t("landing.location")}</p>
          <p className="text-white mb-4">{data.map_address}</p>

          {/* Map */}
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "250px" }}
            center={{
              lat: Number(data.map_latitude),
              lng: Number(data.map_longitude),
            }}
            zoom={14}
          >
            <Marker
              position={{
                lat: Number(data.map_latitude),
                lng: Number(data.map_longitude),
              }}
              icon={{
                url: map_pin,
                scaledSize: new window.google.maps.Size(60, 60),
              }}
            />
          </GoogleMap>
        </div>
      )}

      {data?.status === "paused" ? (
        <div className="text-center">
          <p className="text-xl font-semibold text-white mb-5 pt-5">
            {t("landing.pausedTitle")}
          </p>
          <p className="text-base font-medium text-slate-200 mb-5">
            {t("landing.pausedMessage", { title: data.title })}
          </p>
          <p className="text-base font-medium text-slate-300 mb-2">
            {t("landing.pausedDetail")}
          </p>
          <p className="text-base font-medium text-slate-300 mb-10">
            {t("landing.pausedNote")}
          </p>
        </div>
      ) : data?.status === "closed" ? (
        <div className="text-center">
          <p className="text-xl font-semibold text-white mb-5 pt-5">
            {t("landing.closedTitle")}
          </p>
          <p className="text-base font-medium text-slate-200 mb-5">
            {t("landing.closedMessage", { title: data.title })}
          </p>
          <p className="text-base font-medium text-slate-300 mb-1">
            {t("landing.closedDetail1")}
          </p>
          <p className="text-base font-medium text-slate-300 mb-3">
            {t("landing.closedDetail2")}
          </p>
          <p className="text-base font-medium text-slate-300 mb-10">
            {t("landing.closedDetail3")}
          </p>
        </div>
      ) : (
        <div className="text-center">
          <div>
            <p className="text-base font-medium text-slate-300 mb-5">
              {t("landing.registerHint")}
            </p>
          </div>

          <div className="max-w-3xl mx-auto mt-4 bg-slate-900 border border-white/10 rounded-xl p-4 md:p-6 space-y-5">
            {/* Name + Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div>
                <label className="block text-sm text-slate-300 mb-1">
                  {t("landing.fullName")}
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-1">
                  {t("landing.email")}
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </div>
            </div>

            {/* Phone */}
            <div className="text-left">
              <label className="block text-sm text-slate-300 mb-1">
                {t("landing.whatsappNumber")}
              </label>

              <div className="flex flex-col sm:flex-row gap-2">
                {/* Country Code Dropdown */}
                <div className="w-full sm:w-40">
                  <Select
                    options={countryOptions}
                    value={countryOptions.find(
                      (opt) => opt.value === form.countryCode,
                    )}
                    onChange={(selected) =>
                      handleChange("countryCode", selected.value)
                    }
                    className="text-sm"
                    styles={customSelectStyles}
                  />
                </div>

                {/* Phone Input */}
                <input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder={t("landing.phoneInput")}
                  className="flex-1 bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-3 text-left text-sm text-slate-300">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.acceptTerms}
                  onChange={(e) =>
                    handleChange("acceptTerms", e.target.checked)
                  }
                  className="mt-1 accent-pink-600"
                />
                <span>
                  {t("landing.termsLabel")}{" "}
                  <span className="text-pink-500 underline cursor-pointer">
                    {t("landing.termsLink")}
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.acceptPrivacy}
                  onChange={(e) =>
                    handleChange("acceptPrivacy", e.target.checked)
                  }
                  className="mt-1 accent-pink-600"
                />
                <span>
                  {t("landing.privacyLabel")}{" "}
                  <span className="text-pink-500 underline cursor-pointer">
                    {t("landing.privacyLink")}
                  </span>
                </span>
              </label>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={!isFormValid}
              className={`w-full py-3 rounded-lg font-medium transition ${
                isFormValid
                  ? "bg-pink-600 hover:bg-pink-700"
                  : "bg-pink-200 cursor-not-allowed"
              }`}
            >
              {t("landing.submitBtn")}
            </button>

            <div>
              <p className="text-sm font-medium text-slate-300 mb-5">
                {t("landing.submitHint")}
              </p>
            </div>
          </div>
        </div>
      )}

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default PublicLandingPage;

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#131C31",
    borderColor: state.isFocused
      ? "rgba(236, 72, 153, 0.6)" // pink focus
      : "rgba(255,255,255,0.1)",
    boxShadow: "none",
    "&:hover": {
      borderColor: "rgba(236, 72, 153, 0.6)",
    },
  }),

  menu: (base) => ({
    ...base,
    backgroundColor: "#131C31",
    border: "1px solid rgba(255,255,255,0.1)",
    zIndex: 50,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? "rgba(236, 72, 153, 0.2)"
      : state.isSelected
        ? "rgba(236, 72, 153, 0.4)"
        : "transparent",
    color: "#fff",
    cursor: "pointer",
  }),

  singleValue: (base) => ({
    ...base,
    color: "#fff",
  }),

  input: (base) => ({
    ...base,
    color: "#fff",
  }),

  placeholder: (base) => ({
    ...base,
    color: "#94a3b8", // slate-400
  }),

  dropdownIndicator: (base) => ({
    ...base,
    color: "#94a3b8",
    "&:hover": {
      color: "#fff",
    },
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),
};
